import { useEffect, useRef, useState } from "react";
import { useRefreshTokenExpiry } from "./useRefreshTokenExpiry";

interface UseIdleTimeoutOptions {
	/** Warning time before timeout in milliseconds (default: 5 minutes) */
	warningTime?: number;
	/** Callback when warning threshold is reached */
	onWarning?: () => void;
	/** Callback when timeout is reached */
	onTimeout?: () => void;
	/** Whether the idle timer is enabled */
	enabled?: boolean;
}

interface UseIdleTimeoutReturn {
	/** Remaining time until timeout in milliseconds */
	remainingTime: number;
	/** Whether the warning is active */
	isWarning: boolean;
	/** Whether the timeout has been reached */
	isTimedOut: boolean;
}

/**
 * Hook to detect user idle timeout and provide warnings
 */
export const useSessionTimeout = ({
	warningTime = 5 * 60 * 1000, // 5 minutes warning default
	onWarning,
	onTimeout,
	enabled = true
}: UseIdleTimeoutOptions = {}): UseIdleTimeoutReturn => {
	const [remainingTime, setRemainingTime] = useState(60 * 60 * 1000); // Default 60 minutes
	const [isWarning, setIsWarning] = useState(false);
	const [isTimedOut, setIsTimedOut] = useState(false);
	const { tokenTimeout } = useRefreshTokenExpiry();

	const intervalRef = useRef<NodeJS.Timeout>();
	const lastActivityRef = useRef<number>(Date.now());

	// Main timer effect - starts/restarts the timer when dependencies change
	useEffect(() => {
		if (!enabled) return;

		// Clear existing timer
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		// Start countdown interval
		intervalRef.current = setInterval(() => {
			const now = Date.now();
			const elapsed = now - lastActivityRef.current;
			const remaining = tokenTimeout - elapsed;

			setRemainingTime(Math.max(0, remaining));

			// Check for warning threshold
			if (remaining <= warningTime && remaining > 0 && !isWarning) {
				setIsWarning(true);
				onWarning?.();
			}

			// Check for timeout
			if (remaining <= 0) {
				setIsTimedOut(true);
				setIsWarning(false);
				onTimeout?.();
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
				}
			}
		}, 1000); // Update every second

		// Cleanup function
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [enabled, tokenTimeout, warningTime, onWarning, onTimeout, isWarning]);

	return {
		remainingTime,
		isWarning,
		isTimedOut
	};
};
